import type { LocationDescriptor } from "history";
import type { FC, HTMLProps, ReactNode } from "react";

import { NavLink } from "@shared/react-router-dom";

import { DropdownItem } from "../DropdownItem";

import * as styles from "./DropdownLink.styles";

interface Props extends HTMLProps<HTMLDivElement> {
  /**
   * Sets boilerplate styling for component
   *
   * @default false
   */
  noStyle?: boolean;
  /**
   * sets use of active styling associated with matching url
   *
   *
   */
  noActiveStyling?: boolean;
  /**
   * path prop, use with links within react app
   */
  to?: LocationDescriptor;
  /**
   * path prop, use with links leaving current react app
   */
  href?: string;
  /**
   * HTML standard
   */
  target?: string;
  /**
   * HTML standard
   */
  download?: boolean;
  /**
   * HTML standard
   */
  rel?: string;
  /**
   * Callback fired when component selected
   */
  onSelect?: () => void;
  /**
   * Removes the width truncation text limit
   */
  noTruncation?: boolean;
  /**
   * Content to render within the link
   *
   * @default undefined
   */
  children?: ReactNode;
}

const DropdownLink: FC<Props> = ({
  children,
  to,
  noActiveStyling,
  noStyle,
  download,
  rel,
  target,
  href,
  disabled,
  ...rest
}) => {
  const conditionalStyles = [
    !noStyle && styles.dropdownLink,
    !noStyle && disabled && styles.disabled,
  ];

  const conditionalNavStyles = [
    !noStyle && styles.navLink(noActiveStyling),
    !noStyle && disabled && styles.disabled,
  ];

  if (href) {
    return (
      <DropdownItem css={conditionalStyles} tabIndex={-1} noStyle {...rest}>
        <a
          css={styles.navLink(noActiveStyling)}
          tabIndex={0}
          href={href}
          target={target}
          download={download}
          rel={rel}
        >
          {children}
        </a>
      </DropdownItem>
    );
  }

  return (
    <DropdownItem css={conditionalStyles} tabIndex={-1} noStyle {...rest}>
      <NavLink to={to} css={conditionalNavStyles} end target={target} download={download} rel={rel}>
        {children}
      </NavLink>
    </DropdownItem>
  );
};

export { DropdownLink };
